import React, { useState } from 'react';
import { BIOMECHANICAL_FAULTS } from '../core/biomechanics';
import { BiomechanicalFault } from '../core/types';
import { tennisAudio } from '../core/audio';
import { useI18n } from '../core/i18n';
import {
  Wrench,
  Activity,
  AlertTriangle,
  Sparkles,
  Award,
  CheckCircle2,
  Copy,
  Check,
  Flame,
  Dumbbell,
} from 'lucide-react';

export const BiomechanicsStudioView: React.FC = () => {
  const { t } = useI18n();
  const [selectedStroke, setSelectedStroke] = useState<'forehand' | 'backhand' | 'serve' | 'volley' | 'overhead'>('forehand');
  const [selectedFaultId, setSelectedFaultId] = useState<string>('fh_flying_long');
  const [copied, setCopied] = useState(false);

  // Filter faults by selected stroke
  const availableFaults = BIOMECHANICAL_FAULTS.filter((f) => f.stroke === selectedStroke);
  const activeFault: BiomechanicalFault =
    availableFaults.find((f) => f.id === selectedFaultId) || availableFaults[0] || BIOMECHANICAL_FAULTS[0];

  const handleSelectStroke = (stroke: typeof selectedStroke) => {
    setSelectedStroke(stroke);
    const first = BIOMECHANICAL_FAULTS.find((f) => f.stroke === stroke);
    if (first) {
      setSelectedFaultId(first.id);
    }
    tennisAudio.playHit('topspin');
  };

  const handleCopyPrescription = () => {
    const text = `🎾【欢乐网球 AI 动作诊断处方 · Joy Tennis Biomechanics】
🛠️ 技术环节：${activeFault.stroke.toUpperCase()}
⚠️ 症状表现：${activeFault.symptom}
🧬 动力链阶段：${activeFault.kineticChainStage}
-----------------------------------------
🔍 根因剖析：
${activeFault.rootCauses.map((c, i) => `${i + 1}. ${c}`).join('\n')}

💡 纠错核心要领：
${activeFault.remedyAction}

🌟 职业大师标杆示范 (${activeFault.proComparison.proPlayerExample})：
· 正确动作：${activeFault.proComparison.proExecution}
· 常见误区：${activeFault.proComparison.flawedHabit}

🏋️‍♂️ 专项纠错特训方案：
${activeFault.correctiveDrills.map((d) => `· 【${d.name}】(${d.intensity})\n  ${d.description}`).join('\n\n')}
-----------------------------------------
欢乐网球俱乐部 (Joy Tennis Club) · 唯爱AI公益基金会
🌐 在线AI球技诊断：https://tennis.weiai.ai`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const strokes = [
    { id: 'forehand', name: '现代正手 (Forehand)', icon: '🎾' },
    { id: 'backhand', name: '双手/单手反手 (Backhand)', icon: '🔄' },
    { id: 'serve', name: '发球与切削/Kick (Serve)', icon: '⚡' },
    { id: 'volley', name: '网前截击 (Volley)', icon: '🛡️' },
  ];

  return (
    <div className="w-full max-w-5xl mx-auto p-3 sm:p-5 space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-850 to-slate-900 p-5 rounded-3xl border border-slate-800 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-lime-500/30 to-sky-500/20 border border-lime-400/40 flex items-center justify-center text-lime-400 shadow-glow-ball">
            <Wrench className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-white flex items-center gap-2">
              <span>{t.diagnosticStudio}</span>
              <span className="text-[10px] bg-lime-500/20 text-lime-300 px-2 py-0.5 rounded-full border border-lime-500/40 font-bold">
                ATP/WTA 动力链模型
              </span>
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              输入击球失误痛点，AI自动逆向解构下肢蹬地、髋部转体、拍头滞后与刷球动力链，开具大师级纠错处方
            </p>
          </div>
        </div>

        {/* Copy Prescription Button */}
        <button
          onClick={handleCopyPrescription}
          className="flex items-center space-x-1.5 px-4 py-2 rounded-xl bg-lime-500 hover:bg-lime-400 text-slate-950 font-black text-xs transition-all shadow-md active:scale-95 shrink-0"
        >
          {copied ? <Check className="w-4 h-4 text-slate-950" /> : <Copy className="w-4 h-4" />}
          <span>{copied ? '已复制诊断处方' : '复制微信训练笔记'}</span>
        </button>
      </div>

      {/* 1. Stroke Selector Tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {strokes.map((st) => (
          <button
            key={st.id}
            onClick={() => handleSelectStroke(st.id as any)}
            className={`p-3 rounded-2xl border flex items-center space-x-2.5 transition-all text-left ${
              selectedStroke === st.id
                ? 'bg-slate-850 border-lime-400 text-white shadow-md ring-1 ring-lime-400/40'
                : 'bg-slate-900/70 border-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-850'
            }`}
          >
            <span className="text-xl">{st.icon}</span>
            <span className="text-xs font-bold">{st.name}</span>
          </button>
        ))}
      </div>

      {/* 2. Error Symptoms Chips */}
      <div className="bg-slate-900/60 p-4 rounded-2xl border border-slate-800">
        <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-2.5">
          ⚠️ 常见击球失误症状与困扰 (Select Symptom)
        </label>
        <div className="flex flex-wrap gap-2">
          {availableFaults.map((fault) => {
            const isSelected = activeFault.id === fault.id;
            return (
              <button
                key={fault.id}
                onClick={() => {
                  setSelectedFaultId(fault.id);
                  tennisAudio.playHit('slice');
                }}
                className={`px-3.5 py-2 rounded-xl border text-xs font-bold transition-all flex items-center space-x-1.5 ${
                  isSelected
                    ? 'bg-lime-500 text-slate-950 border-lime-400 shadow-md'
                    : 'bg-slate-950 border-slate-800 text-slate-300 hover:border-slate-700'
                }`}
              >
                {fault.severity === 'injury_risk' && <span>🚨</span>}
                <span>{fault.symptom}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Detailed AI Diagnosis Deck */}
      <div className="bg-slate-900/90 rounded-3xl border border-slate-800 overflow-hidden shadow-2xl space-y-6 p-5 sm:p-6">
        {/* Kinetic Chain Stage & Severity */}
        <div className="flex flex-wrap items-center justify-between gap-2 pb-4 border-b border-slate-800">
          <div>
            <span className="text-xs text-slate-500 font-semibold block">动力链环节 (Kinetic Chain Stage)</span>
            <span className="text-sm font-black text-lime-400 uppercase tracking-wide">
              {activeFault.kineticChainStage}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`text-xs px-2.5 py-1 rounded-full font-bold border ${
                activeFault.severity === 'injury_risk'
                  ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
                  : 'bg-amber-500/20 text-amber-300 border-amber-500/40'
              }`}
            >
              {activeFault.severity === 'injury_risk' ? '⚠️ 伴随伤病风险 (Injury Risk)' : '常见动作缺陷 (Common Fault)'}
            </span>
          </div>
        </div>

        {/* Root Causes List */}
        <div>
          <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2.5 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span>深度成因解构 (Root Causes)</span>
          </h3>
          <ul className="space-y-2">
            {activeFault.rootCauses.map((cause, idx) => (
              <li key={idx} className="flex items-start space-x-2 text-xs text-slate-300 leading-relaxed">
                <span className="text-amber-400 font-mono mt-0.5">•</span>
                <span>{cause}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Master Remedy & Action */}
        <div className="p-4 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 rounded-2xl border border-lime-500/40">
          <h3 className="text-xs font-black uppercase tracking-wider text-lime-400 mb-1.5 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-lime-400" />
            <span>AI教练纠错核心要诀 (Coach Core Remedy)</span>
          </h3>
          <p className="text-xs text-white leading-relaxed font-medium">{activeFault.remedyAction}</p>
        </div>

        {/* Pro Master Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-950 p-4 rounded-2xl border border-slate-850">
          <div>
            <div className="text-[11px] font-bold text-rose-400 mb-1">❌ 业余典型误区习惯</div>
            <p className="text-xs text-slate-400 leading-relaxed">{activeFault.proComparison.flawedHabit}</p>
          </div>
          <div>
            <div className="text-[11px] font-bold text-emerald-400 mb-1 flex items-center gap-1">
              <span>✅ 标杆大师示范: {activeFault.proComparison.proPlayerExample}</span>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">{activeFault.proComparison.proExecution}</p>
          </div>
        </div>

        {/* Corrective Drills */}
        <div>
          <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
            <Dumbbell className="w-4 h-4 text-sky-400" />
            <span>定制化纠错特训方案 (Prescribed Drills)</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {activeFault.correctiveDrills.map((drill, index) => (
              <div key={index} className="p-3.5 bg-slate-950 rounded-2xl border border-slate-800 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black text-white">{drill.name}</span>
                  <span className="text-[10px] bg-slate-850 text-sky-400 px-2 py-0.5 rounded font-mono">
                    {drill.intensity}
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">{drill.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
